import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { withTranslation } from "react-i18next"
import { Box } from "@mui/material"
import ListDataTable from "../../components/Common/list-data-table"
import { apiBuscarVendasItens } from "../../helpers/backend_helper"
import { describeDateTime, describeMoney } from "../../helpers/utils"
import {
    DateColumnFilter,
    describeQuantidade,
    EmptyRowsMessage,
    opcoesSituacao,
    SituacaoBadge
} from "./movimento-vendas-detalhe-cells"

/**
 * Itens da venda selecionada. A consulta é sempre escopada pela empresa e pelo número da
 * venda recebidos em filters — o backend recusa a chamada sem essas chaves. A descrição
 * vem do cardápio; cadastro ausente ou inativo chega sinalizado pelo backend.
 */
const MovimentoVendasItensTable = props => {

    const { filters, t } = props
    const [data, setData] = useState([])

    const colunas = useMemo(() => [
        { header: t("movimentovendas.itens.seq"), accessorKey: "Seq", id: "Seq", size: 130 },
        {
            header: t("movimentovendas.itens.item"), accessorKey: "Item", id: "Item", size: 190,
            // Identifica o componente de item composto. Qual é o item pai fica na coluna
            // "Item principal" e no title do badge.
            Cell: ({ cell, row }) => {
                const itemPrincipal = row.original.Item_P || 0
                if (itemPrincipal <= 0) {
                    return cell?.getValue()
                }
                return (
                    <Box sx={ { display: "flex", alignItems: "center", gap: "0.5rem", whiteSpace: "nowrap" } }>
                        <span>{ cell?.getValue() }</span>
                        <span className="badge badge-soft-info"
                              title={ t("movimentovendas.itens.componente.titulo", { item: itemPrincipal }) }>
                            { t("movimentovendas.itens.componente") }
                        </span>
                    </Box>
                )
            }
        },
        { header: t("movimentovendas.itens.codigo"), accessorKey: "Codigo", id: "Codigo", size: 120 },
        // Vem do cardápio pelo backend (não existe no item): ordena, mas não filtra por coluna.
        {
            header: t("movimentovendas.itens.descricao"), accessorKey: "Descricao", id: "Descricao", size: 260,
            enableColumnFilter: false
        },
        {
            header: t("movimentovendas.itens.data"), accessorKey: "Data", id: "Data", size: 160,
            Cell: ({ cell }) => describeDateTime(cell?.getValue()), sortingFn: "datetime",
            Filter: DateColumnFilter
        },
        {
            header: t("movimentovendas.itens.qtd"), accessorKey: "Qtd", id: "Qtd", size: 130,
            Cell: ({ cell }) => (<Box sx={ { display: "block", textAlign: "right" } }>{ describeQuantidade(cell?.getValue()) }</Box>)
        },
        {
            header: t("movimentovendas.itens.pvenda"), accessorKey: "PVenda", id: "PVenda", size: 150,
            Cell: ({ cell }) => (<Box sx={ { display: "block", textAlign: "right" } }>{ describeMoney(cell?.getValue()) }</Box>)
        },
        {
            header: t("movimentovendas.itens.desconto"), accessorKey: "Desconto", id: "Desconto", size: 130,
            Cell: ({ cell }) => (<Box sx={ { display: "block", textAlign: "right" } }>{ describeMoney(cell?.getValue()) }</Box>)
        },
        { header: t("movimentovendas.itens.altfunc"), accessorKey: "AltFunc", id: "AltFunc", size: 140 },
        {
            header: t("movimentovendas.itens.altdata"), accessorKey: "AltData", id: "AltData", size: 170,
            Cell: ({ cell }) => describeDateTime(cell?.getValue()), sortingFn: "datetime",
            Filter: DateColumnFilter
        },
        {
            header: t("movimentovendas.itens.status"), accessorKey: "Status", id: "Status", size: 150,
            Cell: ({ cell }) => (<SituacaoBadge situacao={ cell?.getValue() } t={ t } />),
            filterSelectOptions: opcoesSituacao(t),
            filterVariant: "multi-select"
        },
        { header: t("movimentovendas.itens.loja"), accessorKey: "Loja", id: "Loja", size: 120 },
        {
            header: t("movimentovendas.itens.itemprincipal"), accessorKey: "Item_P", id: "Item_P", size: 150,
            Cell: ({ cell }) => (cell?.getValue() || 0) > 0 ? cell?.getValue() : "-"
        },
        { header: t("movimentovendas.itens.operador"), accessorKey: "Operador", id: "Operador", size: 130 },
        { header: t("movimentovendas.itens.garcom"), accessorKey: "Func_Garcom", id: "Func_Garcom", size: 130 },
        { header: t("movimentovendas.itens.tipo"), accessorKey: "Tipo", id: "Tipo", size: 130, filterFn: "contains" },
        { header: t("movimentovendas.itens.seqcaixa"), accessorKey: "Seq_Caixa", id: "Seq_Caixa", size: 160 },
        {
            header: t("movimentovendas.itens.pcusto"), accessorKey: "PCusto", id: "PCusto", size: 130,
            Cell: ({ cell }) => (<Box sx={ { display: "block", textAlign: "right" } }>{ describeMoney(cell?.getValue()) }</Box>)
        },
        {
            header: t("movimentovendas.itens.promocao"), accessorKey: "Promocao", id: "Promocao", size: 140,
            Cell: ({ cell }) => (<Box sx={ { display: "block", textAlign: "right" } }>{ describeMoney(cell?.getValue()) }</Box>)
        },
        {
            header: t("movimentovendas.itens.comissao"), accessorKey: "Comissao", id: "Comissao", size: 140,
            Cell: ({ cell }) => (<Box sx={ { display: "block", textAlign: "right" } }>{ describeMoney(cell?.getValue()) }</Box>)
        },
        {
            header: t("movimentovendas.itens.datarecebto"), accessorKey: "Data_Recebto", id: "Data_Recebto", size: 180,
            Cell: ({ cell }) => describeDateTime(cell?.getValue()), sortingFn: "datetime",
            Filter: DateColumnFilter
        },
        { header: t("movimentovendas.itens.lojapedido"), accessorKey: "Loja_Pedido", id: "Loja_Pedido", size: 150 }
    ], [t])

    return (
        <ListDataTable
            searchFn={ params => apiBuscarVendasItens(params) }
            advancedFilters={ filters }
            resetPageOnFiltersChange={ true }
            columns={ colunas }
            data={ data }
            setData={ setData }
            columnVisibility={ {
                PCusto: false,
                Promocao: false,
                Comissao: false,
                Data_Recebto: false,
                Loja_Pedido: false
            } }
            defaultSorting={ [{ id: "Item", desc: false }] }
            renderEmptyRowsFallback={ () => (<EmptyRowsMessage mensagem={ t("movimentovendas.itens.vazio") } />) }
        />
    )
}

MovimentoVendasItensTable.propTypes = {
    filters: PropTypes.array,
    t: PropTypes.func
}

export default withTranslation()(MovimentoVendasItensTable)
